import { BLOCK_TYPES } from '../world/BlockTypes.js'
import { getSwordSprite } from '../rendering/Sprites.js'
import { PLAYER_SPRITES, PLAYER_WALK_SPRITES } from '../rendering/CharacterSpriteSheets.js'
import {
  normalizeHeartDamage,
  normalizeHeartValue
} from './HeartHealth.js'

export class Player {
  constructor(game, saveData = null) {
    this.game = game
    
    // Position (2D)
    this.x = 0
    this.y = 0
    this.velocity = { x: 0, y: 0 }
    
    // Direction: 'up', 'down', 'left', 'right'
    this.facing = 'down'
    
    // Health is stored in semantic hearts and snaps to quarter-heart increments.
    this.maxHearts = 3
    this.hearts = 3
    
    // Movement
    this.moveSpeed = 5.4
    this.isMoving = false
    
    // Walking animation
    this.walkAnimTime = 0
    this.walkAnimSpeed = 8 // frames per second
    this.walkFrame = 0
    
    // Combat
    this.isAttacking = false
    this.attackDuration = 0.2
    this.attackTimer = 0
    this.attackCooldown = 0.3
    this.attackCooldownTimer = 0
    this.attackRange = 1.5
    this.attackDamage = 10
    this.isInvulnerable = false
    this.invulnerabilityDuration = 0.75
    this.invulnerabilityTimer = 0
    
    // Equipment
    this.hasSword = false
    this.weaponId = null
    this.swordDamage = 10
    this.shieldId = null
    this.shieldDefenseHearts = 0
    this.hasBow = false
    this.arrows = {}
    this.coins = 0
    this.fruit = {}
    this.isShielding = false
    this.isSitting = false
    this.isSleeping = false
    this.arrowProjectiles = []
    this.arrowCooldown = 0
    this.arrowCooldownDuration = 10
    this.arrowBurstCount = 0
    this.arrowBurstSize = 10
    this.shieldCooldown = 0
    this.shieldCooldownDuration = 10
    this.shieldHoldTime = 0
    this.shieldHoldDuration = 10
    this.shieldWasHeld = false
    
    // Interaction
    this.interactRange = 2

    // Keys
    this.keys = 0
    this.darkKingKeys = 0
    
    // Zone tracking
    this.currentZone = 'forest'
    this.currentLevelIndex = 0
    this.highestLevelReached = 0
    this.spawnPoint = { x: 0, y: 0 }
    this.pendingRespawnPoint = null
    
    // Dev mode
    this.godMode = false
    
    // Collision size
    this.radius = 0.4
    
    // Visual
    this.swordSwingProgress = 0 // 0 to 1 during attack

    // New adventures begin safely inside the player's home.
    if (!saveData && this.game.world && this.game.world.zones.length > 0) {
      const startZone = this.game.world.zones[0]
      const homeSpawn = this.game.world.home?.interiorSpawn || startZone.spawnPoint
      this.x = homeSpawn.x
      this.y = homeSpawn.y
      this.currentZone = startZone.name
      this.currentLevelIndex = 0
      this.spawnPoint = { ...startZone.spawnPoint }
    }
    
    if (saveData) {
      this.deserialize(saveData)
    }
  }
  
  get position() {
    return { x: this.x, y: this.y }
  }
  
  giveStartingItems() {
    // Player starts with nothing — sword is found in the world.
  }
  
  update(delta) {
    this.updateShield(delta)
    this.arrowCooldown = Math.max(0, this.arrowCooldown - delta)
    this.updateArrows(delta)
    this.updateMovement(delta)
    this.updateCombat(delta)
    this.updateInvulnerability(delta)
    this.checkZone()
    this.checkKeyPickup()
    this.checkHeartPickup()
  }

  updateShield(delta) {
    const held = this.shieldId !== null && this.game.input?.isShieldHeld?.() === true
    this.shieldCooldown = Math.max(0, this.shieldCooldown - delta)

    if (this.isShielding) {
      this.shieldHoldTime += delta
      if (this.shieldHoldTime >= this.shieldHoldDuration) {
        this.isShielding = false
        this.shieldHoldTime = 0
        this.shieldCooldown = this.shieldCooldownDuration
      }
      this.shieldWasHeld = held
      return
    }

    if (held && !this.shieldWasHeld && this.shieldCooldown === 0) {
      this.isShielding = true
      this.shieldHoldTime = delta
      if (this.shieldHoldTime >= this.shieldHoldDuration) {
        this.isShielding = false
        this.shieldHoldTime = 0
        this.shieldCooldown = this.shieldCooldownDuration
      }
    }

    this.shieldWasHeld = held
  }
  
  updateMovement(delta) {
    const input = this.game.input
    if (this.isSleeping) {
      this.isMoving = false
      this.walkAnimTime = 0
      this.walkFrame = 0
      return
    }
    const movement = input.getMovementVector()
    let moveX = Number.isFinite(movement?.x) ? movement.x : 0
    let moveY = Number.isFinite(movement?.y) ? movement.y : 0

    // Sitting is an intentional furniture state. Movement cannot accidentally
    // pull the player out of a couch; B is the explicit stand-up action.
    if (this.isSitting) {
      this.isMoving = false
      this.walkAnimTime = 0
      this.walkFrame = 0
      return
    }
    
    // Update facing direction based on movement (Zelda style - 4 directions)
    if (moveY < 0) this.facing = 'up'
    else if (moveY > 0) this.facing = 'down'
    else if (moveX < 0) this.facing = 'left'
    else if (moveX > 0) this.facing = 'right'
    
    // Track if player is moving
    this.isMoving = (moveX !== 0 || moveY !== 0)

    // Update walk animation
    if (this.isMoving) {
      this.walkAnimTime += delta * this.walkAnimSpeed
      this.walkFrame = Math.floor(this.walkAnimTime) % 4
    } else {
      this.walkAnimTime = 0
      this.walkFrame = 0
    }
    
    // Clamp combined input while preserving analog joystick strength.
    const inputMagnitude = Math.hypot(moveX, moveY)
    if (inputMagnitude > 1) {
      moveX /= inputMagnitude
      moveY /= inputMagnitude
    }
    
    // Apply speed
    let speed = this.moveSpeed
    this.walkAnimSpeed = 8
    if (input.isRunActive?.()) {
      speed *= 1.65
      this.walkAnimSpeed = 12
    }
    
    // Debug mode: 5x speed
    if (this.game.renderer.debugZoom) {
      speed *= 5
    }
    
    // Calculate new position
    const newX = this.x + moveX * speed * delta
    const newY = this.y + moveY * speed * delta
    
    // Collision detection
    this.handleCollision(newX, newY)
  }
  
  handleCollision(newX, newY) {
    // Check X movement
    if (!this.collidesAt(newX, this.y)) {
      this.x = newX
    }
    
    // Check Y movement
    if (!this.collidesAt(this.x, newY)) {
      this.y = newY
    }
  }
  
  collidesAt(x, y) {
    // Debug mode: no collision
    if (this.game.renderer.debugZoom) return false
    
    // Check collision with world tiles
    const checkPoints = [
      { x: x - this.radius, y: y - this.radius },
      { x: x + this.radius, y: y - this.radius },
      { x: x - this.radius, y: y + this.radius },
      { x: x + this.radius, y: y + this.radius }
    ]
    
    for (const point of checkPoints) {
      const tileX = Math.floor(point.x)
      const tileY = Math.floor(point.y)
      if (this.game.world.isLevelAdvanceLockedAt?.(point.x, point.y)) {
        this.game.world.notifyLevelAdvanceLocked?.()
        return true
      }
      const tile = this.game.world.getTileAt(tileX, tileY)
      if (tile === BLOCK_TYPES.LOCKED_DOOR) {
        const darkKingDoor = this.game.world.isDarkKingDoorAt?.(tileX, tileY) === true
        const availableKeys = darkKingDoor ? this.darkKingKeys : this.keys
        if (availableKeys > 0) {
          if (darkKingDoor) this.darkKingKeys -= 1
          else this.keys -= 1
          this.game.world.unlockDoorAt(tileX, tileY)
          continue
        }
        return true
      }
      if (this.game.world.isSolid(point.x, point.y)) {
        return true
      }
    }

    if (this.game.itemManager?.collidesWithShop(x, y, this.radius)) {
      return true
    }
    if (this.game.world.collidesWithHome?.(x, y, this.radius)) {
      return true
    }
    if (this.game.world.collidesWithPostgameVillage?.(x, y, this.radius)) {
      return true
    }

    // Block movement through living enemies
    if (this.game.enemyManager) {
      for (const enemy of this.game.enemyManager.enemies) {
        if (!enemy.isAlive || enemy.pendingDeath) continue
        const dx = enemy.x - x
        const dy = enemy.y - y
        const dist = Math.sqrt(dx * dx + dy * dy)
        if (dist < this.radius + enemy.radius) {
          return true
        }
      }
    }
    
    return false
  }
  
  attack() {
    if (!this.hasSword) return
    if (this.attackCooldownTimer > 0 || this.isAttacking) return
    
    this.isAttacking = true
    this.attackTimer = this.attackDuration
    this.attackCooldownTimer = this.attackCooldown
    this.swordSwingProgress = 0
    
    // Get attack damage from equipped weapon
    const damage = this.getAttackDamage()
    
    // Get attack position based on facing direction
    let attackX = this.x
    let attackY = this.y
    
    switch (this.facing) {
      case 'up':    attackY -= 1; break
      case 'down':  attackY += 1; break
      case 'left':  attackX -= 1; break
      case 'right': attackX += 1; break
    }
    
    // Check for enemies in attack area (in front of player)
    if (this.game.enemyManager) {
      for (const enemy of this.game.enemyManager.enemies) {
        if (!enemy.isAlive) continue
        
        const dx = enemy.x - attackX
        const dy = enemy.y - attackY
        const dist = Math.sqrt(dx * dx + dy * dy)
        
        // Hit enemies within range of the sword swing point
        if (dist < this.attackRange && this.hasLineOfSightTo(enemy.x, enemy.y)) {
          enemy.takeDamage(damage)
        }
      }
    }
  }

  hasLineOfSightTo(targetX, targetY) {
    const dx = targetX - this.x
    const dy = targetY - this.y
    const dist = Math.sqrt(dx * dx + dy * dy)
    if (dist <= 0.01) return true

    const step = 0.2
    const dirX = dx / dist
    const dirY = dy / dist
    const steps = Math.floor(dist / step)

    for (let i = 1; i < steps; i++) {
      const sampleX = this.x + dirX * i * step
      const sampleY = this.y + dirY * i * step
      if (this.game.world.isSolid(sampleX, sampleY)) {
        return false
      }
    }

    return true
  }
  
  getAttackDamage() {
    return this.swordDamage
  }
  
  updateCombat(delta) {
    if (this.attackCooldownTimer > 0) {
      this.attackCooldownTimer -= delta
    }
    
    if (this.isAttacking) {
      this.attackTimer -= delta
      // Sword swing animation progress (0 to 1)
      this.swordSwingProgress = 1 - (this.attackTimer / this.attackDuration)
      
      if (this.attackTimer <= 0) {
        this.isAttacking = false
        this.swordSwingProgress = 0
      }
    }
  }
  
  updateInvulnerability(delta) {
    if (this.invulnerabilityTimer > 0) {
      this.invulnerabilityTimer -= delta
      if (this.invulnerabilityTimer <= 0) {
        this.isInvulnerable = false
      }
    }
  }
  
  takeDamage(amount) {
    if (this.isInvulnerable || this.godMode || this.game.renderer?.debugZoom) return 0

    const shieldReduction = this.isShielding
      ? this.shieldDefenseHearts
      : 0
    const damage = normalizeHeartDamage(Math.max(0, amount - shieldReduction))
    if (damage <= 0) return 0

    this.hearts = normalizeHeartValue(this.hearts - damage, this.maxHearts)
    this.isInvulnerable = true
    this.invulnerabilityTimer = this.invulnerabilityDuration
    
    if (this.hearts <= 0) {
      this.hearts = 0
      this.die()
    }

    return damage
  }
  
  heal(amount) {
    if (!Number.isFinite(amount) || amount <= 0) return 0
    const previousHearts = this.hearts
    this.hearts = normalizeHeartValue(this.hearts + amount, this.maxHearts)
    return this.hearts - previousHearts
  }

  addHeartContainer(amount = 1) {
    if (!Number.isFinite(amount) || amount <= 0) return 0
    const containers = Math.max(1, Math.floor(amount))
    this.maxHearts += containers
    this.hearts = this.maxHearts
    return containers
  }

  equipWeapon(weapon) {
    const damage = Number(weapon?.damage)
    if (!Number.isFinite(damage) || damage <= 0) return false

    this.hasSword = true
    if (damage < this.swordDamage) return false

    const changed = damage > this.swordDamage || this.weaponId !== weapon.id
    this.swordDamage = damage
    this.weaponId = weapon.id || this.weaponId || 'unknown_sword'
    return changed
  }

  equipShield(shield) {
    const defense = Number(shield?.defenseHearts)
    if (!Number.isFinite(defense) || defense <= 0 || defense < this.shieldDefenseHearts) return false
    this.shieldId = shield.id || 'shield'
    this.shieldDefenseHearts = defense
    return true
  }

  addArrows(id = 'arrow', count = 1) {
    const amount = Math.max(1, Math.floor(count))
    this.arrows[id] = (this.arrows[id] || 0) + amount
    return amount
  }

  equipBow() {
    const changed = !this.hasBow
    this.hasBow = true
    return changed
  }

  shootArrow() {
    if (!this.hasBow || this.arrowCooldown > 0) return false
    const arrowTypes = ['light_arrow', 'shadow_arrow', 'fire_arrow', 'crystal_arrow', 'bone_arrow', 'arrow']
    const arrowId = arrowTypes.find(id => (this.arrows[id] || 0) > 0)
    if (!arrowId) return false

    const directions = {
      up: { x: 0, y: -1 },
      down: { x: 0, y: 1 },
      left: { x: -1, y: 0 },
      right: { x: 1, y: 0 }
    }
    const direction = directions[this.facing] || directions.down
    const damageByType = {
      arrow: 12,
      bone_arrow: 15,
      crystal_arrow: 20,
      fire_arrow: 18,
      shadow_arrow: 22,
      light_arrow: 25
    }

    this.arrows[arrowId] -= 1
    this.arrowBurstCount += 1
    if (this.arrowBurstCount >= this.arrowBurstSize) {
      this.arrowBurstCount = 0
      this.arrowCooldown = this.arrowCooldownDuration
    }
    this.arrowProjectiles.push({
      x: this.x + direction.x * 0.65,
      y: this.y + direction.y * 0.65,
      dirX: direction.x,
      dirY: direction.y,
      damage: damageByType[arrowId],
      lifetime: 2.5,
      type: arrowId
    })
    return true
  }

  updateArrows(delta) {
    for (let i = this.arrowProjectiles.length - 1; i >= 0; i--) {
      const arrow = this.arrowProjectiles[i]
      const travelX = arrow.dirX * 12 * delta
      const travelY = arrow.dirY * 12 * delta
      const steps = Math.max(1, Math.ceil(Math.hypot(travelX, travelY) / 0.2))
      const stepX = travelX / steps
      const stepY = travelY / steps
      arrow.lifetime -= delta

      let hit = false
      for (let step = 0; step < steps && !hit; step += 1) {
        arrow.x += stepX
        arrow.y += stepY

        // Check the path, not only the frame's endpoint. At the capped 0.1s
        // frame delta an arrow travels farther than one tile, so endpoint-only
        // collision could pass through a wall and hit an enemy behind it.
        if (this.game.world.isSolid(arrow.x, arrow.y)) {
          hit = true
          break
        }

        for (const enemy of this.game.enemyManager?.enemies || []) {
          if (!enemy.isAlive || enemy.pendingDeath) continue
          if (Math.hypot(enemy.x - arrow.x, enemy.y - arrow.y) < enemy.radius + 0.25) {
            enemy.takeDamage(arrow.damage)
            hit = true
            break
          }
        }
      }

      if (hit || arrow.lifetime <= 0) {
        this.arrowProjectiles.splice(i, 1)
      }
    }
  }

  addCoins(count = 1) {
    const amount = Math.max(1, Math.floor(count))
    this.coins += amount
    return amount
  }

  addFruit(id, count = 1) {
    if (!id) return 0
    const amount = Math.max(1, Math.floor(count))
    this.fruit[id] = (this.fruit[id] || 0) + amount
    return amount
  }

  eatBanana() {
    return this.eatFruit('banana')
  }

  eatFruit(preferred = null) {
    if (this.hearts >= this.maxHearts) return false
    const healing = {
      healthPotion: 2,
      apple: 0.5,
      banana: 1,
      dragonFruit: 2,
      goldenPineapple: 3,
      goldenKiwi: 4.5,
      moonMelon: 4,
      watermelon: 5,
      starFruit: 6
    }
    if (preferred && (this.fruit[preferred] || 0) <= 0) return false
    const id = preferred || [
      'healthPotion',
      'banana',
      'apple',
      'dragonFruit',
      'goldenPineapple',
      'goldenKiwi',
      'moonMelon',
      'watermelon',
      'starFruit'
    ]
      .find(type => (this.fruit[type] || 0) > 0)
    if (!id) return false
    this.fruit[id] -= 1
    this.heal(healing[id])
    return true
  }

  spendCoins(count) {
    if (!Number.isFinite(count) || count <= 0 || this.coins < count) return false
    this.coins -= Math.floor(count)
    return true
  }

  checkHeartPickup() {
    if (!this.game.itemManager) return
    const items = this.game.itemManager.droppedItems
    for (let i = items.length - 1; i >= 0; i--) {
      const item = items[i]
      if (item.data.type !== 'heart') continue
      const dx = item.x - this.x
      const dy = item.y - this.y
      if (Math.sqrt(dx * dx + dy * dy) < 0.8) {
        this.heal(1)
        items.splice(i, 1)
      }
    }
  }

  addKey(count = 1) {
    this.keys += count
  }

  addDarkKingKey(count = 1) {
    this.darkKingKeys += count
  }

  checkKeyPickup() {
    const tileX = Math.floor(this.x)
    const tileY = Math.floor(this.y)
    const tile = this.game.world.getTileAt(tileX, tileY)
    if (tile === BLOCK_TYPES.KEY) {
      this.addKey(1)
      this.game.world.clearTileToGround(tileX, tileY)
    }
  }
  
  eat() {}
  
  die() {
    this.pendingRespawnPoint = this.game.world.getDeathRespawnPoint?.(this.x, this.y) || null
    const zone = this.game.getCurrentZone?.()
    if (zone?.spawnPoint) {
      const levelIndex = this.game.world.zones.indexOf(zone)
      this.currentZone = zone.name
      if (levelIndex >= 0) this.currentLevelIndex = levelIndex
      this.spawnPoint = { ...zone.spawnPoint }
    }
    this.game.showDeathScreen()
  }
  
  respawn() {
    const preserveDarkKingFight = this.pendingRespawnPoint !== null
    const levelStart = this.pendingRespawnPoint ||
      this.game.world.zones[this.currentLevelIndex]?.spawnPoint
      || this.spawnPoint
    this.pendingRespawnPoint = null
    this.spawnPoint = { ...levelStart }
    this.x = levelStart.x
    this.y = levelStart.y
    this.hearts = this.maxHearts
    this.velocity = { x: 0, y: 0 }
    this.isInvulnerable = true
    this.invulnerabilityTimer = 2
    return preserveDarkKingFight
  }
  
  placeTile() {}
  
  breakTile() {
    // Legacy method - use mineTile instead
    const mouseWorld = this.game.renderer.screenToWorld(this.game.input.mouseX, this.game.input.mouseY)
    const tileX = Math.floor(mouseWorld.x)
    const tileY = Math.floor(mouseWorld.y)
    this.mineTile(tileX, tileY)
  }
  
  mineTile() {}
  
  
  
  selectHotbarSlot() {}
  
  scrollHotbar() {}
  
  checkZone() {
    const zone = this.game.getCurrentZone()
    if (zone) {
      const levelIndex = this.game.world.zones.findIndex(candidate => candidate.name === zone.name)
      if (levelIndex >= 0) this.currentLevelIndex = levelIndex
      if (zone.name === this.currentZone) return
      this.currentZone = zone.name
      this.highestLevelReached = Math.max(this.highestLevelReached, levelIndex)
      // Deaths restart at the authored entrance of the current level.
      this.spawnPoint = { ...zone.spawnPoint }
    }
  }
  
  render(renderer) {
    // Flash when invulnerable
    if (this.isInvulnerable && Math.floor(this.invulnerabilityTimer * 10) % 2 === 0) {
      return
    }
    
    // Get appropriate sprite based on movement and facing direction
    let sprite
    if (this.isMoving) {
      // Use walking animation frames
      const walkSprites = PLAYER_WALK_SPRITES[this.facing] || PLAYER_WALK_SPRITES.down
      sprite = walkSprites[this.walkFrame]
    } else {
      // Use static standing sprite
      sprite = PLAYER_SPRITES[this.facing] || PLAYER_SPRITES.down
    }
    
    // Tiny head bob when walking (offset y by small amount)
    const headBob = this.isMoving ? Math.sin(this.walkAnimTime * Math.PI) * 0.02 : 0
    
    // Debug mode: float above blocks
    let renderY = this.y - 0.5 - headBob
    if (this.game.renderer.debugZoom) {
      renderY += 0.3 // Float visibly above blocks
    }
    
    renderer.drawSprite(
      sprite,
      this.x - 0.5,
      renderY,
      this.isSleeping ? Math.PI / 2 : 0,
      1
    )

    for (const arrow of this.arrowProjectiles) {
      const horizontal = arrow.dirX !== 0
      renderer.fillRect(
        arrow.x - (horizontal ? 0.35 : 0.06),
        arrow.y - (horizontal ? 0.06 : 0.35),
        horizontal ? 0.7 : 0.12,
        horizontal ? 0.12 : 0.7,
        arrow.type === 'fire_arrow'
          ? '#ff7a24'
          : arrow.type === 'shadow_arrow'
            ? '#b15cff'
            : arrow.type === 'crystal_arrow'
              ? '#72e4ff'
              : arrow.type === 'bone_arrow'
                ? '#f0dfb8'
                : '#9b6334'
      )
    }

    if (this.isShielding) {
      const offsets = {
        up: { x: -0.45, y: -0.72, w: 0.9, h: 0.18 },
        down: { x: -0.45, y: 0.54, w: 0.9, h: 0.18 },
        left: { x: -0.72, y: -0.45, w: 0.18, h: 0.9 },
        right: { x: 0.54, y: -0.45, w: 0.18, h: 0.9 }
      }
      const shield = offsets[this.facing] || offsets.down
      renderer.fillRect(this.x + shield.x, this.y + shield.y, shield.w, shield.h, '#8fd4e8')
      renderer.fillRect(
        this.x + shield.x + shield.w * 0.3,
        this.y + shield.y + shield.h * 0.3,
        shield.w * 0.4,
        shield.h * 0.4,
        '#e7f8ff'
      )
    }
    
    // Draw sword if attacking (and we have one)
    if (this.isAttacking && this.hasSword) {
      // Sword swings in an arc AROUND the player
      const swingArc = Math.PI * 0.9 // 162 degree arc
      const orbitRadius = 0.7 // Distance from player center to sword hilt
      
      // Base angle points in the facing direction
      let baseAngle = 0
      switch (this.facing) {
        case 'up':    baseAngle = -Math.PI / 2; break
        case 'down':  baseAngle = Math.PI / 2; break
        case 'left':  baseAngle = Math.PI; break
        case 'right': baseAngle = 0; break
      }
      
      // Swing from one side to the other (e.g., from upper-right to lower-right when facing right)
      const swingOffset = -swingArc / 2 + this.swordSwingProgress * swingArc
      const orbitAngle = baseAngle + swingOffset
      
      // Position sword hilt on the arc around player
      const swordX = this.x + Math.cos(orbitAngle) * orbitRadius
      const swordY = this.y + Math.sin(orbitAngle) * orbitRadius
      
      // Sword blade points outward from center (perpendicular to orbit + slight forward tilt)
      const bladeAngle = orbitAngle + Math.PI * 0.15 // Slight forward tilt
      
      // Draw pixel art sword sprite with rotation
      renderer.drawSprite(getSwordSprite(this.weaponId), swordX - 0.5, swordY - 0.5, bladeAngle)
    }
  }
  
  serialize() {
    return {
      x: this.x,
      y: this.y,
      facing: this.facing,
      hearts: this.hearts,
      maxHearts: this.maxHearts,
      hasSword: this.hasSword,
      weaponId: this.weaponId,
      keys: this.keys,
      darkKingKeys: this.darkKingKeys,
      swordDamage: this.swordDamage,
      shieldId: this.shieldId,
      shieldDefenseHearts: this.shieldDefenseHearts,
      hasBow: this.hasBow,
      arrows: { ...this.arrows },
      coins: this.coins,
      fruit: { ...this.fruit },
      currentZone: this.currentZone,
      currentLevelIndex: this.currentLevelIndex,
      highestLevelReached: this.highestLevelReached,
      spawnPoint: { ...this.spawnPoint }
    }
  }
  
  deserialize(data) {
    if (!data) return
    
    this.x = Number.isFinite(data.x) ? data.x : Number.NaN
    this.y = Number.isFinite(data.y) ? data.y : Number.NaN
    this.facing = ['up', 'down', 'left', 'right'].includes(data.facing)
      ? data.facing
      : 'down'
    const savedMaxHearts = Number.isFinite(data.maxHearts) ? data.maxHearts : 3
    // The authored game cannot reach this ceiling. It prevents a malformed
    // save from asking the HUD to render millions of heart elements.
    this.maxHearts = Math.min(20, Math.max(1, Math.round(savedMaxHearts)))
    const savedHearts = Number.isFinite(data.hearts) ? data.hearts : this.maxHearts
    this.hearts = normalizeHeartValue(savedHearts, this.maxHearts, this.maxHearts)
    this.hasSword = data.hasSword === true
    this.weaponId = typeof data.weaponId === 'string'
      ? data.weaponId
      : this.hasSword
        ? 'legacy_sword'
        : null
    this.keys = data.keys || 0
    this.darkKingKeys = data.darkKingKeys || 0
    if (Number.isFinite(data.swordDamage) && data.swordDamage > 0) {
      this.swordDamage = data.swordDamage
    }
    this.shieldId = typeof data.shieldId === 'string' ? data.shieldId : null
    this.shieldDefenseHearts = Number.isFinite(data.shieldDefenseHearts)
      ? Math.max(0, data.shieldDefenseHearts)
      : 0
    this.arrows = data.arrows && typeof data.arrows === 'object'
      ? { ...data.arrows }
      : {}
    this.hasBow = data.hasBow === true || (
      data.hasBow === undefined &&
      Object.values(this.arrows).some(count => Number(count) > 0)
    )
    this.coins = Number.isFinite(data.coins) ? Math.max(0, Math.floor(data.coins)) : 0
    this.fruit = data.fruit && typeof data.fruit === 'object'
      ? { ...data.fruit }
      : {}
    const savedLevelIndex = Number.isInteger(data.currentLevelIndex)
      ? data.currentLevelIndex
      : this.game.world.zones.findIndex(zone => zone.name === data.currentZone)
    const positionZone = Number.isFinite(this.x) && Number.isFinite(this.y)
      ? this.game.world.getZoneAt?.(this.x, this.y)
      : null
    const positionLevelIndex = positionZone
      ? this.game.world.zones.indexOf(positionZone)
      : -1
    this.currentLevelIndex = savedLevelIndex >= 0 &&
      savedLevelIndex < this.game.world.zones.length
      ? savedLevelIndex
      : Math.max(0, positionLevelIndex)
    const currentZone = this.game.world.zones[this.currentLevelIndex]
    this.currentZone = currentZone?.name || data.currentZone || 'forest'
    const savedLevel = Number.isInteger(data.highestLevelReached)
      ? data.highestLevelReached
      : this.currentLevelIndex
    this.highestLevelReached = Math.max(0, savedLevel)
    this.spawnPoint = currentZone?.spawnPoint
      ? { ...currentZone.spawnPoint }
      : { ...data.spawnPoint }

    const safePosition = this.game.world.getSafePlayerLoadPosition?.(
      this.x,
      this.y,
      this.currentLevelIndex
    )
    if (safePosition) {
      this.x = safePosition.x
      this.y = safePosition.y
    } else if (this.game.world.isInsideHomeCouchAt?.(this.x, this.y)) {
      this.x = this.game.world.home.interiorSpawn.x
      this.y = this.game.world.home.interiorSpawn.y
    }
    this.isSitting = false
    this.isSleeping = false
    this.sittingVillageHouseId = null
    
    
  }
}
